/**
 * DashboardMobile — мобильная раскладка дашборда (этап метрик): шапка +
 * сегмент периода, тёмный MRR-hero, карточки 2-в-ряд (активные/триал),
 * «По рискам» и конверсия/отменено. Навигацию даёт AppShell (резерв
 * pr-[60px] под гамбургер). Тренды-графики — следующим этапом.
 */

import type {ReactNode} from 'react';

import type {SubscriptionStatus} from '@services/clients';
import type {DashboardSummary} from '@services/dashboard';
import {Loader} from '@components/ui/Loader';

import {type DashPeriod} from '../Dashboard.utils';
import {PeriodSegment} from '../PeriodSegment';
import {RisksList} from '../RisksList';
import {SplitBar} from '../SplitBar';
import {TrendsSection} from '../TrendsSection';

import {MrrHero} from './MrrHero';

type DashboardMobileProps = {
  summary?: DashboardSummary;
  isLoading: boolean;
  period: DashPeriod;
  onPeriod: (p: DashPeriod) => void;
  onPickRisk: (status: SubscriptionStatus) => void;
};

const NUM = 'font-mono text-[26px] font-semibold -tracking-[.02em]';

export const DashboardMobile = ({
  summary,
  isLoading,
  period,
  onPeriod,
  onPickRisk,
}: DashboardMobileProps) => (
  <div className="flex min-h-full flex-col">
    <header className="pb-3 pl-[18px] pr-[60px] pt-3">
      <h1 className="text-[23px] font-bold -tracking-[.02em]">Дашборд</h1>
      <div className="mt-0.5 text-xs text-muted-foreground">
        Обзор · {summary?.dateLabel ?? '—'}
      </div>
    </header>

    <div className="px-4 pb-2">
      <PeriodSegment period={period} onChange={onPeriod} mobile />
    </div>

    <div className="flex flex-col gap-3 px-4 pb-6 pt-2">
      {isLoading || !summary ? (
        <div className="flex min-h-[260px] items-center justify-center">
          <Loader label="Загружаем метрики…" />
        </div>
      ) : (
        <>
          <MrrHero mrr={summary.mrr} />

          <div className="flex gap-3">
            <Mini label="Активных">
              <div className={`mt-1.5 ${NUM}`}>{summary.active.total}</div>
              <div className="mt-2.5">
                <SplitBar solo={summary.active.solo} clinic={summary.active.clinic} compact />
              </div>
            </Mini>
            <Mini label="В триале">
              <div className={`mt-1.5 ${NUM}`}>{summary.trial.total}</div>
              <div className="mt-2.5 flex gap-1.5">
                <MiniBox value={summary.trial.noCard} label="без карты" warn />
                <MiniBox value={summary.trial.withCard} label="с картой" />
              </div>
            </Mini>
          </div>

          <div className="rounded-[14px] border border-skr-line bg-card px-4 py-[15px]">
            <div className="text-[13px] font-semibold">По рискам</div>
            <RisksList risks={summary.risks} onPick={onPickRisk} />
          </div>

          <div className="flex gap-3">
            <Mini label="Конверсия">
              <div className={`mt-1.5 ${NUM}`}>
                {summary.conversion.pct}
                <span className="text-base text-muted-foreground">%</span>
              </div>
              <div className="mt-2.5 h-[7px] overflow-hidden rounded-[5px] bg-skr-line2">
                <div
                  className="h-full rounded-[5px] bg-primary"
                  style={{width: `${summary.conversion.pct}%`}}
                />
              </div>
              <div className="mt-1.5 text-[11px] text-skr-label">
                {summary.conversion.converted} из {summary.conversion.leads} заявок
              </div>
            </Mini>
            <Mini label="Отменено">
              <div className="mt-1.5 flex items-baseline gap-1.5">
                <span className={NUM}>{summary.cancelled.total}</span>
                <span className="text-[11px] font-semibold text-skr-danger">за месяц</span>
              </div>
              <div className="mt-[13px] text-[11px] text-skr-label">
                churn · {summary.cancelled.solo} соло · {summary.cancelled.clinic} клин.
              </div>
            </Mini>
          </div>

          <TrendsSection />
        </>
      )}
    </div>
  </div>
);

const Mini = ({label, children}: {label: string; children: ReactNode}) => (
  <div className="flex-1 rounded-[14px] border border-skr-line bg-card px-4 py-[15px]">
    <div className="text-xs text-muted-foreground">{label}</div>
    {children}
  </div>
);

const MiniBox = ({value, label, warn}: {value: number; label: string; warn?: boolean}) => (
  <div
    className={`flex-1 rounded-[7px] px-[7px] py-[5px] ${warn ? 'bg-[#FBF3E4]' : 'bg-[#F5F3EF]'}`}
  >
    <div className={`font-mono text-sm font-semibold ${warn ? 'text-skr-warn' : 'text-[#403D38]'}`}>
      {value}
    </div>
    <div className={`text-[9.5px] ${warn ? 'text-[#9A7B36]' : 'text-muted-foreground'}`}>
      {label}
    </div>
  </div>
);
